import { computed, onScopeDispose, ref, watch } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { REMOVE_FAVORITE_SITTER } from '@/graphql/provider/profile/queries';
import { PARENT_PROFILE_UNFAVORITED_SITTER_EVENT } from '@/lib/constants/favoriteSitterEvents';
import { sendSegmentEvent } from '@/lib/segment';
import type { Ref } from 'vue';
import type { FavoriteSittersConnection, FavoriteSittersVariables, RemoveFavoriteSitterMutation } from './types';

export function useFavoriteRemoval(
  connection: Readonly<Ref<FavoriteSittersConnection | null>>,
  variables: Readonly<Ref<FavoriteSittersVariables>>,
  refreshFavorites: () => Promise<void>
) {
  const { mutate: removeFavoriteSitter } = useMutation<RemoveFavoriteSitterMutation, { providerId: string }>(
    REMOVE_FAVORITE_SITTER
  );
  const removedIds = ref(new Set<string>());
  const removingSitterId = ref<string | null>(null);
  const favoriteActionError = ref(false);
  const removedSitterName = ref('');
  const showRemovedNotification = ref(false);
  let disposed = false;

  const visibleEdges = computed(() =>
    (connection.value?.edges ?? []).filter(({ node }) => !removedIds.value.has(node.sitterIdForProfile))
  );
  const totalCount = computed(() => {
    const current = connection.value;
    if (!current) return 0;
    return Math.max(0, current.totalCount - (current.edges.length - visibleEdges.value.length));
  });

  watch(variables, () => removedIds.value.clear());
  onScopeDispose(() => {
    disposed = true;
  });

  async function removeFavorite(sitterId: string): Promise<boolean> {
    if (removingSitterId.value) return false;
    const name = connection.value?.edges.find(({ node }) => node.sitterIdForProfile === sitterId)?.node.name ?? 'Caregiver';
    removingSitterId.value = sitterId;
    favoriteActionError.value = false;
    showRemovedNotification.value = false;

    try {
      const removed = await removeFavoriteSitter({ providerId: sitterId });
      if (!removed?.data?.profile.removeFavoriteSitter.success) throw new Error('Could not remove favorite sitter');
      sendSegmentEvent(PARENT_PROFILE_UNFAVORITED_SITTER_EVENT, { sitter_uuid: sitterId });
      if (disposed) return false;
      removedIds.value.add(sitterId);
      removedSitterName.value = name;
      showRemovedNotification.value = true;
      void refreshFavorites();
      return true;
    } catch (error) {
      if (!disposed) favoriteActionError.value = true;
      console.error(error);
      return false;
    } finally {
      removingSitterId.value = null;
    }
  }

  function hideRemovedNotification(): void {
    showRemovedNotification.value = false;
  }

  return {
    visibleEdges,
    totalCount,
    removingSitterId,
    favoriteActionError,
    removedSitterName,
    showRemovedNotification,
    removeFavorite,
    hideRemovedNotification,
  };
}
